import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";

const EmployeeForm = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  editingEmployee,
  departments,
  designations,
  employees,
  resetForm,
}) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) resetForm(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editingEmployee ? "Edit Employee" : "Add New Employee"}
          </DialogTitle>
          <DialogDescription>
            {editingEmployee ? "Update employee information" : "Fill in the employee details"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="John"
                className="input-field"
                required
                data-testid="employee-firstname-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Doe"
                className="input-field"
                required
                data-testid="employee-lastname-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                className="input-field"
                required
                data-testid="employee-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 9876543210"
                className="input-field"
                data-testid="employee-phone-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Employee Code</Label>
              <Input
                value={formData.employee_code}
                onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                placeholder="EMP001"
                className="input-field"
                data-testid="employee-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Biometric ID</Label>
              <Input
                value={formData.biometric_id}
                onChange={(e) => setFormData({ ...formData, biometric_id: e.target.value })}
                placeholder="BIO123"
                className="input-field"
                data-testid="employee-biometric-input"
              />
            </div>
          </div>

          {/* Organization */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Select
                value={formData.department_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, department_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="input-field" data-testid="employee-department-select">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Department</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Designation</Label>
              <Select
                value={formData.designation_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, designation_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="input-field" data-testid="employee-designation-select">
                  <SelectValue placeholder="Select designation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Designation</SelectItem>
                  {designations.map((desg) => (
                    <SelectItem key={desg.id} value={desg.id}>{desg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reports To</Label>
              <Select
                value={formData.reports_to || "none"}
                onValueChange={(value) => setFormData({ ...formData, reports_to: value === "none" ? "" : value })}
              >
                <SelectTrigger className="input-field" data-testid="employee-reportsto-select">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {employees.filter(e => e.id !== editingEmployee?.id).map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Work Location</Label>
              <Input
                value={formData.work_location}
                onChange={(e) => setFormData({ ...formData, work_location: e.target.value })}
                placeholder="Office / Remote"
                className="input-field"
                data-testid="employee-location-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="is_department_head"
              checked={formData.is_department_head}
              onCheckedChange={(checked) => setFormData({ ...formData, is_department_head: checked })}
              data-testid="employee-depthead-checkbox"
            />
            <Label htmlFor="is_department_head" className="cursor-pointer">Department Head</Label>
          </div>

          {/* System Role (for HR access) */}
          <div className="space-y-2">
            <Label>System Access Role</Label>
            <Select
              value={formData.system_role || "employee"}
              onValueChange={(value) => setFormData({ ...formData, system_role: value })}
            >
              <SelectTrigger className="input-field" data-testid="employee-role-select">
                <SelectValue placeholder="Select system role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee (Self-service only)</SelectItem>
                <SelectItem value="hr_assistant">HR Assistant (Limited admin access)</SelectItem>
                <SelectItem value="hr_manager">HR Manager (Full HR access)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {formData.system_role === "hr_assistant" 
                ? "Can manage: Attendance, Employees, Onboarding, Documents" 
                : formData.system_role === "hr_manager"
                ? "Full access to all HR modules"
                : "Access to self-service portal only"}
            </p>
          </div>

          {/* Dates & Salary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hire Date</Label>
              <Input
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                className="input-field"
                data-testid="employee-hiredate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Salary</Label>
              <Input
                type="number"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                placeholder="50000"
                className="input-field"
                data-testid="employee-salary-input"
              />
            </div>
          </div>

          {/* Personal Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Birthday</Label>
              <Input
                type="date"
                value={formData.birthday}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                className="input-field"
                data-testid="employee-birthday-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Blood Group</Label>
              <Select
                value={formData.blood_group || "none"}
                onValueChange={(value) => setFormData({ ...formData, blood_group: value === "none" ? "" : value })}
              >
                <SelectTrigger className="input-field" data-testid="employee-bloodgroup-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Set</SelectItem>
                  {["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map(bg => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main St"
              className="input-field"
              data-testid="employee-address-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pin Code</Label>
              <Input
                value={formData.pin_code}
                onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })}
                placeholder="123456"
                className="input-field"
                data-testid="employee-pincode-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Father/Husband Name</Label>
              <Input
                value={formData.father_or_husband_name}
                onChange={(e) => setFormData({ ...formData, father_or_husband_name: e.target.value })}
                placeholder="Guardian name"
                className="input-field"
                data-testid="employee-guardian-input"
              />
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Emergency Contact Person</Label>
              <Input
                value={formData.emergency_contact_person}
                onChange={(e) => setFormData({ ...formData, emergency_contact_person: e.target.value })}
                placeholder="Contact name"
                className="input-field"
                data-testid="employee-emergency-person-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Emergency Contact Number</Label>
              <Input
                value={formData.emergency_contact_number}
                onChange={(e) => setFormData({ ...formData, emergency_contact_number: e.target.value })}
                placeholder="+91 9876543210"
                className="input-field"
                data-testid="employee-emergency-number-input"
              />
            </div>
          </div>

          <Button type="submit" className="w-full btn-primary" data-testid="save-employee-btn">
            {editingEmployee ? "Update Employee" : "Add Employee"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeForm;
